import { Children, useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Box, Drawer, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius } from '../../../theme/theme';
import { getWorkspaceDrawerPaperSx } from './WorkflowWorkspacePrimitives';

interface WorkspacePageShellProps {
  rail?: ReactNode;
  content?: ReactNode;
  detail?: ReactNode;
  children?: ReactNode;
  storageKey?: string;
  defaultRailWidth?: number;
  defaultDetailWidth?: number;
  minRailWidth?: number;
  maxRailWidth?: number;
  minDetailWidth?: number;
  maxDetailWidth?: number;
  railPresetWidth?: number;
  detailPresetWidth?: number;
  overlayRail?: boolean;
  overlayDetail?: boolean;
  onOverlayRailClose?: () => void;
  onOverlayDetailClose?: () => void;
  railResizeLabel?: string;
  detailResizeLabel?: string;
  tourRefs?: {
    railResizeRef?: RefObject<HTMLDivElement | null>;
    detailResizeRef?: RefObject<HTMLDivElement | null>;
  };
}

type ResizeTarget = 'rail' | 'detail';

interface DragState {
  startX: number;
  startRailWidth: number;
  startDetailWidth: number;
  target: ResizeTarget;
}

const DEFAULT_MIN_RAIL_WIDTH = 208;
const DEFAULT_MAX_RAIL_WIDTH = 420;
const DEFAULT_MIN_DETAIL_WIDTH = 360;
const DEFAULT_MAX_DETAIL_WIDTH = 760;
const MIN_CONTENT_WIDTH = 480;
const RESIZE_STEP = 24;
const DEFAULT_RAIL_RESIZE_LABEL = 'Resize rail';
const DEFAULT_DETAIL_RESIZE_LABEL = 'Resize detail';

function ShellSlot({
  children,
  testId,
}: {
  children: ReactNode;
  testId: string;
}) {
  return (
    <Box
      data-testid={testId}
      sx={{
        width: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flex: '1 1 auto',
        overflow: 'hidden',
        '& > *': {
          flex: '1 1 auto',
          minWidth: 0,
          minHeight: 0,
          width: '100%',
          height: '100%',
        },
      }}
    >
      {children}
    </Box>
  );
}

function readStoredWidths(
  storageKey: string | undefined,
  defaultRailWidth: number,
  defaultDetailWidth: number
) {
  if (!storageKey || typeof window === 'undefined') {
    return {
      railWidth: defaultRailWidth,
      detailWidth: defaultDetailWidth,
    };
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {
        railWidth: defaultRailWidth,
        detailWidth: defaultDetailWidth,
      };
    }

    const parsed = JSON.parse(raw) as {
      railWidth?: number;
      detailWidth?: number;
    };

    return {
      railWidth:
        typeof parsed.railWidth === 'number' && Number.isFinite(parsed.railWidth)
          ? parsed.railWidth
          : defaultRailWidth,
      detailWidth:
        typeof parsed.detailWidth === 'number' && Number.isFinite(parsed.detailWidth)
          ? parsed.detailWidth
          : defaultDetailWidth,
    };
  } catch {
    return {
      railWidth: defaultRailWidth,
      detailWidth: defaultDetailWidth,
    };
  }
}

function clampRailWidth(
  width: number,
  shellWidth: number,
  detailWidth: number,
  hasDetail: boolean,
  minRailWidth: number,
  maxRailWidth: number
) {
  const maxAllowed = hasDetail
    ? Math.min(maxRailWidth, shellWidth - detailWidth - MIN_CONTENT_WIDTH)
    : Math.min(maxRailWidth, shellWidth - MIN_CONTENT_WIDTH);

  return Math.max(minRailWidth, Math.min(width, maxAllowed));
}

function clampDetailWidth(
  width: number,
  shellWidth: number,
  railWidth: number,
  hasRail: boolean,
  minDetailWidth: number,
  maxDetailWidth: number
) {
  const maxAllowed = hasRail
    ? Math.min(maxDetailWidth, shellWidth - railWidth - MIN_CONTENT_WIDTH)
    : Math.min(maxDetailWidth, shellWidth - MIN_CONTENT_WIDTH);

  return Math.max(minDetailWidth, Math.min(width, maxAllowed));
}

function ResizeHandle({
  label,
  dataTestId,
  targetRef,
  onPointerDown,
  onKeyDown,
  onDoubleClick,
  valueNow,
  valueMin,
  valueMax,
}: {
  label: string;
  dataTestId: string;
  targetRef?: RefObject<HTMLDivElement | null>;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
  valueNow: number;
  valueMin: number;
  valueMax: number;
}) {
  const theme = useTheme();

  return (
    <Box
      ref={targetRef}
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      aria-valuenow={Math.round(valueNow)}
      tabIndex={0}
      data-testid={dataTestId}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={onDoubleClick}
      sx={{
        width: 20,
        flexShrink: 0,
        cursor: 'col-resize',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        position: 'relative',
        outline: 'none',
        '&::before': {
          content: '""',
          width: 2,
          borderRadius: `${borderRadius.full}px`,
          backgroundColor: alpha(theme.palette.divider, 0.2),
          transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
          my: 0.65,
        },
        '&:hover::before': {
          backgroundColor: alpha(theme.palette.primary.main, 0.5),
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
        '&:focus-visible::before': {
          backgroundColor: alpha(theme.palette.primary.main, 0.72),
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
      }}
    />
  );
}

export default function WorkspacePageShell({
  rail,
  content,
  detail,
  children,
  storageKey,
  defaultRailWidth = 240,
  defaultDetailWidth = 560,
  minRailWidth = DEFAULT_MIN_RAIL_WIDTH,
  maxRailWidth = DEFAULT_MAX_RAIL_WIDTH,
  minDetailWidth = DEFAULT_MIN_DETAIL_WIDTH,
  maxDetailWidth = DEFAULT_MAX_DETAIL_WIDTH,
  railPresetWidth,
  detailPresetWidth,
  overlayRail = false,
  overlayDetail = false,
  onOverlayRailClose,
  onOverlayDetailClose,
  railResizeLabel = DEFAULT_RAIL_RESIZE_LABEL,
  detailResizeLabel = DEFAULT_DETAIL_RESIZE_LABEL,
  tourRefs,
}: WorkspacePageShellProps) {
  const theme = useTheme();
  const shellRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const [shellWidth, setShellWidth] = useState(0);
  const [activeResize, setActiveResize] = useState<ResizeTarget | null>(null);
  const [panelWidths, setPanelWidths] = useState(() =>
    readStoredWidths(storageKey, defaultRailWidth, defaultDetailWidth)
  );

  const childSlots = Children.toArray(children).filter((child) =>
    typeof child === 'string' ? child.trim().length > 0 : true
  );
  const inferredSlots =
    rail !== undefined || content !== undefined || detail !== undefined
      ? {
          slotRail: rail ?? null,
          slotContent: content ?? null,
          slotDetail: detail ?? null,
        }
      : childSlots.length === 1
        ? {
            slotRail: null,
            slotContent: childSlots[0] ?? null,
            slotDetail: null,
          }
        : childSlots.length === 2
          ? {
              slotRail: childSlots[0] ?? null,
              slotContent: childSlots[1] ?? null,
              slotDetail: null,
            }
          : {
              slotRail: childSlots[0] ?? null,
              slotContent: childSlots[1] ?? null,
              slotDetail: childSlots[2] ?? null,
            };
  const { slotRail, slotContent, slotDetail } = inferredSlots;
  const hasRail = Boolean(slotRail);
  const hasDetail = Boolean(slotDetail);

  useEffect(() => {
    setPanelWidths(readStoredWidths(storageKey, defaultRailWidth, defaultDetailWidth));
  }, [defaultDetailWidth, defaultRailWidth, storageKey]);

  useEffect(() => {
    if (typeof railPresetWidth !== 'number' || !Number.isFinite(railPresetWidth)) {
      return;
    }

    setPanelWidths((current) => ({
      ...current,
      railWidth: railPresetWidth,
    }));
  }, [railPresetWidth]);

  useEffect(() => {
    if (typeof detailPresetWidth !== 'number' || !Number.isFinite(detailPresetWidth)) {
      return;
    }

    setPanelWidths((current) => ({
      ...current,
      detailWidth: detailPresetWidth,
    }));
  }, [detailPresetWidth]);

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(panelWidths));
    } catch {
      // Ignore storage failures; resizing still works for the current session.
    }
  }, [panelWidths, storageKey]);

  useEffect(() => {
    const node = shellRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      setShellWidth(entry.contentRect.width);
    });

    observer.observe(node);
    setShellWidth(node.getBoundingClientRect().width);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!shellWidth) {
      return;
    }

    setPanelWidths((current) => {
      let nextRailWidth = current.railWidth;
      let nextDetailWidth = current.detailWidth;

      if (hasRail) {
        nextRailWidth = clampRailWidth(
          nextRailWidth,
          shellWidth,
          nextDetailWidth,
          hasDetail,
          minRailWidth,
          maxRailWidth
        );
      }

      if (hasDetail) {
        nextDetailWidth = clampDetailWidth(
          nextDetailWidth,
          shellWidth,
          nextRailWidth,
          hasRail,
          minDetailWidth,
          maxDetailWidth
        );
      }

      if (
        nextRailWidth === current.railWidth &&
        nextDetailWidth === current.detailWidth
      ) {
        return current;
      }

      return {
        railWidth: nextRailWidth,
        detailWidth: nextDetailWidth,
      };
    });
  }, [hasDetail, hasRail, maxDetailWidth, maxRailWidth, minDetailWidth, minRailWidth, shellWidth]);

  const commitResize = useCallback(
    (target: ResizeTarget, delta: number) => {
      if (!shellWidth) {
        return;
      }

      setPanelWidths((current) => {
        if (target === 'rail') {
          return {
            ...current,
            railWidth: clampRailWidth(
              current.railWidth + delta,
              shellWidth,
              current.detailWidth,
              hasDetail,
              minRailWidth,
              maxRailWidth
            ),
          };
        }

        return {
          ...current,
          detailWidth: clampDetailWidth(
            current.detailWidth + delta,
            shellWidth,
            current.railWidth,
            hasRail,
            minDetailWidth,
            maxDetailWidth
          ),
        };
      });
    },
    [hasDetail, hasRail, maxDetailWidth, maxRailWidth, minDetailWidth, minRailWidth, shellWidth]
  );

  const resetResize = useCallback(
    (target: ResizeTarget) => {
      if (target === 'rail') {
        setPanelWidths((current) => ({
          ...current,
          railWidth: defaultRailWidth,
        }));
        return;
      }

      setPanelWidths((current) => ({
        ...current,
        detailWidth: defaultDetailWidth,
      }));
    },
    [defaultDetailWidth, defaultRailWidth]
  );

  const handleResizeKeyDown = useCallback(
    (target: ResizeTarget) => (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? RESIZE_STEP * 2 : RESIZE_STEP;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        commitResize(target, target === 'rail' ? step : -step);
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        commitResize(target, target === 'rail' ? -step : step);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        resetResize(target);
      }
    },
    [commitResize, resetResize]
  );

  const beginResize = useCallback(
    (target: ResizeTarget) => (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!shellWidth) {
        return;
      }

      event.preventDefault();
      dragStateRef.current = {
        startX: event.clientX,
        startRailWidth: panelWidths.railWidth,
        startDetailWidth: panelWidths.detailWidth,
        target,
      };
      setActiveResize(target);
    },
    [panelWidths.detailWidth, panelWidths.railWidth, shellWidth]
  );

  useEffect(() => {
    if (!activeResize) {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || !shellWidth) {
        return;
      }

      const delta = event.clientX - dragState.startX;

      if (dragState.target === 'rail') {
        setPanelWidths((current) => ({
          ...current,
          railWidth: clampRailWidth(
            dragState.startRailWidth + delta,
            shellWidth,
            current.detailWidth,
            hasDetail,
            minRailWidth,
            maxRailWidth
          ),
        }));
        return;
      }

      setPanelWidths((current) => ({
        ...current,
        detailWidth: clampDetailWidth(
          dragState.startDetailWidth - delta,
          shellWidth,
          current.railWidth,
          hasRail,
          minDetailWidth,
          maxDetailWidth
        ),
      }));
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      setActiveResize(null);
    };

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      dragStateRef.current = null;
    };
  }, [activeResize, hasDetail, hasRail, maxDetailWidth, maxRailWidth, minDetailWidth, minRailWidth, shellWidth]);

  const railWidth = clampRailWidth(
    panelWidths.railWidth,
    shellWidth || Number.MAX_SAFE_INTEGER,
    panelWidths.detailWidth,
    hasDetail,
    minRailWidth,
    maxRailWidth
  );
  const detailWidth = clampDetailWidth(
    panelWidths.detailWidth,
    shellWidth || Number.MAX_SAFE_INTEGER,
    railWidth,
    hasRail,
    minDetailWidth,
    maxDetailWidth
  );

  const renderInlineRail = hasRail && !overlayRail;
  const renderInlineDetail = hasDetail && !overlayDetail;
  const renderOverlayRail = hasRail && overlayRail;
  const renderOverlayDetail = hasDetail && overlayDetail;

  return (
    <Box
      ref={shellRef}
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        overflow: 'hidden',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        position: 'relative',
      }}
    >
      {renderInlineRail ? (
        <Box
          data-testid="workspace-shell-rail"
          sx={{
            width: railWidth,
            minWidth: 0,
            flexShrink: 0,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          <ShellSlot testId="workspace-shell-slot-rail">{rail}</ShellSlot>
        </Box>
      ) : null}

      {renderInlineRail ? (
        <ResizeHandle
          label={railResizeLabel}
          dataTestId="workspace-shell-rail-resizer"
          targetRef={tourRefs?.railResizeRef}
          onPointerDown={beginResize('rail')}
          onKeyDown={handleResizeKeyDown('rail')}
          onDoubleClick={() => resetResize('rail')}
          valueNow={railWidth}
          valueMin={minRailWidth}
          valueMax={maxRailWidth}
        />
      ) : null}

      <Box
        data-testid="workspace-shell-content"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <ShellSlot testId="workspace-shell-slot-content">{slotContent}</ShellSlot>
      </Box>

      {renderInlineDetail ? (
        <ResizeHandle
          label={detailResizeLabel}
          dataTestId="workspace-shell-detail-resizer"
          targetRef={tourRefs?.detailResizeRef}
          onPointerDown={beginResize('detail')}
          onKeyDown={handleResizeKeyDown('detail')}
          onDoubleClick={() => resetResize('detail')}
          valueNow={detailWidth}
          valueMin={minDetailWidth}
          valueMax={maxDetailWidth}
        />
      ) : null}

      {renderInlineDetail ? (
        <Box
          data-testid="workspace-shell-detail"
          sx={{
            width: detailWidth,
            minWidth: 0,
            flexShrink: 0,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          <ShellSlot testId="workspace-shell-slot-detail">{detail}</ShellSlot>
        </Box>
      ) : null}

      {renderOverlayRail ? (
        <Drawer
          anchor="left"
          open={true}
          onClose={onOverlayRailClose}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: [
              getWorkspaceDrawerPaperSx(theme, 'left'),
              {
              width: railWidth,
            }],
          }}
        >
          <Box
            data-testid="workspace-shell-rail"
            sx={{
              width: railWidth,
              minWidth: 0,
              display: 'flex',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <ShellSlot testId="workspace-shell-slot-rail">{rail}</ShellSlot>
          </Box>
        </Drawer>
      ) : null}

      {renderOverlayDetail ? (
        <Drawer
          anchor="right"
          open={true}
          onClose={onOverlayDetailClose}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: [
              getWorkspaceDrawerPaperSx(theme, 'right'),
              {
              width: detailWidth,
            }],
          }}
        >
          <Box
            data-testid="workspace-shell-detail"
            sx={{
              width: detailWidth,
              minWidth: 0,
              display: 'flex',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <ShellSlot testId="workspace-shell-slot-detail">{detail}</ShellSlot>
          </Box>
        </Drawer>
      ) : null}
    </Box>
  );
}
